import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { Card, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

import {
  baoCaoGetTongHopNhanVienAction,
  baoCaoClearTongHopNhanVien,
} from '@app/store/reducers/baocao';

import { URI_INTERNAL } from '@app/utils/pathLocations';
import { rangeDateReport, messageToast } from '@app/utils/define';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getErrMsg = (error) => {
  if (!error) return '';
  let msg =
    (error && error.message) ||
    (typeof error === 'string' ? error : '') ||
    (error && error.title) ||
    '';

  if (!msg && error && error.errors) {
    msg = Object.values(error.errors).flat().join('; ');
  }
  return msg;
};

const RANGE_CUSTOM = 'Custom Range';
const DEFAULT_RANGE_LABEL = '7 ngày trước';
// const TEAM_FILTER_NO_TEAM = '__NO_TEAM__';
const RANGE_QUERY_EMPTY = '__EMPTY__';

const RANGE_OPTIONS = [
  'Hôm nay',
  'Hôm qua',
  '7 ngày trước',
  '30 ngày trước',
  'Tháng này',
  'Tháng trước',
  '3 tháng trước',
  '6 tháng trước',
  'Năm',
  RANGE_CUSTOM,
];

const toYMD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDDMMYYYY = (ymd) => {
  if (!ymd) return '';
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(ymd);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB');
};

const getRangeFromTo = (label) => {
  if (!label || label === RANGE_CUSTOM) return { from: '', to: '' };

  const pair = rangeDateReport?.[label];
  if (!Array.isArray(pair) || pair.length !== 2) return { from: '', to: '' };

  return {
    from: toYMD(pair[0]),
    to: toYMD(pair[1]),
  };
};

const getDefaultRangeState = () => {
  const { from, to } = getRangeFromTo(DEFAULT_RANGE_LABEL);

  return {
    rangeLabel: DEFAULT_RANGE_LABEL,
    appliedRangeLabel: DEFAULT_RANGE_LABEL,
    fromDate: from,
    toDate: to,
    appliedFrom: from,
    appliedTo: to,
  };
};

const getRangeStateFromSearch = (search = '') => {
  const sp = new URLSearchParams(search || '');
  const hasRangeState = sp.has('RangeLabel') || sp.has('From') || sp.has('To');

  if (!hasRangeState) return null;

  const rawLabel = sp.get('RangeLabel') || '';
  const from = sp.get('From') || '';
  const to = sp.get('To') || '';

  if (rawLabel === RANGE_QUERY_EMPTY) {
    return {
      rangeLabel: '',
      appliedRangeLabel: '',
      fromDate: '',
      toDate: '',
      appliedFrom: '',
      appliedTo: '',
    };
  }

  const normalizedLabel = rawLabel || ((from || to) ? RANGE_CUSTOM : '');

  return {
    rangeLabel: normalizedLabel,
    appliedRangeLabel: normalizedLabel,
    fromDate: from,
    toDate: to,
    appliedFrom: from,
    appliedTo: to,
  };
};

const BaoCaoTongHopNhanVien = ({
  commonFrom = '',
  commonTo = '',
  commonRangeLabel = '',
  commonVersion = 0,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const queryTeamFilter = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');
    const value = sp.get('filterTeamId') || '';

    if (value === '0') return '';

    return value;
  }, [location.search]);

  const [teamFilter, setTeamFilter] = useState(queryTeamFilter);

  useEffect(() => {
    setTeamFilter(queryTeamFilter);
  }, [queryTeamFilter]);

  const replaceSearchParams = (mutator) => {
    const sp = new URLSearchParams(location.search || '');
    mutator(sp);

    const qs = sp.toString();
    history.replace({
      pathname: location.pathname,
      search: qs ? `?${qs}` : '',
    });
  };

  const updateTeamFilterQuery = (nextTeamFilter = '') => {
    replaceSearchParams((sp) => {
      if (!nextTeamFilter) sp.delete('filterTeamId');
      else sp.set('filterTeamId', String(nextTeamFilter));
    });
  };

  const updateRangeFilterQuery = ({
    nextRangeLabel = '',
    nextAppliedRangeLabel = '',
    nextAppliedFrom = '',
    nextAppliedTo = '',
  }) => {
    replaceSearchParams((sp) => {
      sp.delete('RangeLabel');
      sp.delete('From');
      sp.delete('To');

      const normalizedLabel = nextAppliedRangeLabel || nextRangeLabel || '';

      if (!normalizedLabel && !nextAppliedFrom && !nextAppliedTo) {
        sp.set('RangeLabel', RANGE_QUERY_EMPTY);
        return;
      }

      if (normalizedLabel) sp.set('RangeLabel', normalizedLabel);
      if (nextAppliedFrom) sp.set('From', nextAppliedFrom);
      if (nextAppliedTo) sp.set('To', nextAppliedTo);
    });
  };

  const returnUrl = useMemo(
    () => encodeURIComponent(`${location.pathname}${location.search || ''}`),
    [location.pathname, location.search]
  );

  const {
    tongHopNhanVien,
    loadingTongHopNhanVien,
    errorTongHopNhanVien,
  } = useSelector((s) => s.baocao || {});

  const queryRangeState = useMemo(
    () => getRangeStateFromSearch(location.search || ''),
    [location.search]
  );

  const hasQueryRangeState = !!queryRangeState;

  const defaultRangeState = useMemo(() => getDefaultRangeState(), []);

  const externalCommonRangeState = useMemo(() => {
    const hasExternal =
      commonVersion > 0 || !!commonRangeLabel || !!commonFrom || !!commonTo;

    if (!hasExternal) return null;

    const normalizedLabel =
      commonRangeLabel || ((commonFrom || commonTo) ? RANGE_CUSTOM : '');

    return {
      rangeLabel: normalizedLabel,
      appliedRangeLabel: normalizedLabel,
      fromDate: commonFrom || '',
      toDate: commonTo || '',
      appliedFrom: commonFrom || '',
      appliedTo: commonTo || '',
    };
  }, [commonVersion, commonRangeLabel, commonFrom, commonTo]);

  const initialRangeState =
    queryRangeState || externalCommonRangeState || defaultRangeState;

  const [rangeLabel, setRangeLabel] = useState(initialRangeState.rangeLabel);
  const [appliedRangeLabel, setAppliedRangeLabel] = useState(
    initialRangeState.appliedRangeLabel
  );

  const [fromDate, setFromDate] = useState(initialRangeState.fromDate);
  const [toDate, setToDate] = useState(initialRangeState.toDate);

  const [appliedFrom, setAppliedFrom] = useState(initialRangeState.appliedFrom);
  const [appliedTo, setAppliedTo] = useState(initialRangeState.appliedTo);

  useEffect(() => {
    if (!hasQueryRangeState) return;

    setRangeLabel(queryRangeState.rangeLabel);
    setAppliedRangeLabel(queryRangeState.appliedRangeLabel);
    setFromDate(queryRangeState.fromDate);
    setToDate(queryRangeState.toDate);
    setAppliedFrom(queryRangeState.appliedFrom);
    setAppliedTo(queryRangeState.appliedTo);
  }, [hasQueryRangeState, queryRangeState]);

useEffect(() => {
  if (hasQueryRangeState) return;
  if (!externalCommonRangeState) return;

  setRangeLabel(externalCommonRangeState.rangeLabel);
  setAppliedRangeLabel(externalCommonRangeState.appliedRangeLabel);
  setFromDate(externalCommonRangeState.fromDate);
  setToDate(externalCommonRangeState.toDate);
  setAppliedFrom(externalCommonRangeState.appliedFrom);
  setAppliedTo(externalCommonRangeState.appliedTo);
}, [hasQueryRangeState, externalCommonRangeState]);

  useEffect(() => {
    dispatch(
      baoCaoGetTongHopNhanVienAction({
        From: appliedFrom || '',
        To: appliedTo || '',
      })
    );

    return () => {
      dispatch(baoCaoClearTongHopNhanVien());
    };
  }, [dispatch, appliedFrom, appliedTo]);

  useEffect(() => {
    if (!errorTongHopNhanVien) return;
    toast.error(
      getErrMsg(errorTongHopNhanVien) || 'Không thể tải dữ liệu báo cáo chi tiết nhân viên.'
    );
  }, [errorTongHopNhanVien]);

  const handleChangeRange = (e) => {
    const v = e.target.value || '';
    setRangeLabel(v);

    if (!v) {
      setFromDate('');
      setToDate('');
      setAppliedFrom('');
      setAppliedTo('');
      setAppliedRangeLabel('');

      updateRangeFilterQuery({
        nextRangeLabel: '',
        nextAppliedRangeLabel: '',
        nextAppliedFrom: '',
        nextAppliedTo: '',
      });
      return;
    }

    if (v !== RANGE_CUSTOM) {
      const { from, to } = getRangeFromTo(v);

      setFromDate(from);
      setToDate(to);
      setAppliedFrom(from);
      setAppliedTo(to);
      setAppliedRangeLabel(v);

      updateRangeFilterQuery({
        nextRangeLabel: v,
        nextAppliedRangeLabel: v,
        nextAppliedFrom: from,
        nextAppliedTo: to,
      });
      return;
    }

    setFromDate(appliedFrom || '');
    setToDate(appliedTo || '');
  };

  const handleApplyCustomRange = () => {
    if (rangeLabel === RANGE_CUSTOM && !fromDate && !toDate) {
      toast.error(messageToast?.messageToastChooseDateError || 'Mời chọn ngày');
      return;
    }

    let f = fromDate || '';
    let t = toDate || '';

    if (f && t && t < f) {
      const tmp = f;
      f = t;
      t = tmp;
    }

    setAppliedFrom(f);
    setAppliedTo(t);
    setAppliedRangeLabel(RANGE_CUSTOM);

    updateRangeFilterQuery({
      nextRangeLabel: RANGE_CUSTOM,
      nextAppliedRangeLabel: RANGE_CUSTOM,
      nextAppliedFrom: f,
      nextAppliedTo: t,
    });
  };

  const handleChangeTeamFilter = (e) => {
    const v = e.target.value || '';
    setTeamFilter(v);
    updateTeamFilterQuery(v);
  };

  const handleClearFilter = () => {
    setRangeLabel('');
    setFromDate('');
    setToDate('');
    setAppliedRangeLabel('');
    setAppliedFrom('');
    setAppliedTo('');

    setTeamFilter('');

    replaceSearchParams((sp) => {
      sp.delete('filterTeamId');
      sp.delete('RangeLabel');
      sp.delete('From');
      sp.delete('To');
      sp.set('RangeLabel', RANGE_QUERY_EMPTY);
    });
  };

  const rows = useMemo(() => {
    if (!Array.isArray(tongHopNhanVien)) return [];

    return tongHopNhanVien.map((x) => ({
      nhanVienId: x?.nhanVienId ?? x?.NhanVienId ?? 0,
      nguoiDungId: x?.nguoiDungId ?? x?.NguoiDungId ?? '',
      teamId: x?.teamId ?? x?.TeamId ?? null,
      tenTeam: x?.tenTeam ?? x?.TenTeam ?? '',
      tenThanhVien: x?.tenThanhVien ?? x?.TenThanhVien ?? '',
      tongCongViec: toNum(x?.tongCongViec ?? x?.TongCongViec ?? 0),
      hoanThanh: toNum(x?.hoanThanh ?? x?.HoanThanh ?? 0),
      dangThucHien: toNum(x?.dangThucHien ?? x?.DangThucHien ?? 0),

      phanMem: toNum(x?.phanMem ?? x?.PhanMem ?? 0),
      phanMemHoanThanh: toNum(x?.phanMemHoanThanh ?? x?.PhanMemHoanThanh ?? 0),
      phanMemDangThucHien: toNum(x?.phanMemDangThucHien ?? x?.PhanMemDangThucHien ?? 0),

      xuLyYeuCau: toNum(x?.xuLyYeuCau ?? x?.XuLyYeuCau ?? 0),
      xuLyYeuCauHoanThanh: toNum(x?.xuLyYeuCauHoanThanh ?? x?.XuLyYeuCauHoanThanh ?? 0),
      xuLyYeuCauDangThucHien: toNum(x?.xuLyYeuCauDangThucHien ?? x?.XuLyYeuCauDangThucHien ?? 0),

      hoTro: toNum(x?.hoTro ?? x?.HoTro ?? 0),
      hoTroHoanThanh: toNum(x?.hoTroHoanThanh ?? x?.HoTroHoanThanh ?? 0),
      hoTroDangThucHien: toNum(x?.hoTroDangThucHien ?? x?.HoTroDangThucHien ?? 0),

      nhiemVuKhac: toNum(x?.nhiemVuKhac ?? x?.NhiemVuKhac ?? 0),
      nhiemVuKhacHoanThanh: toNum(x?.nhiemVuKhacHoanThanh ?? x?.NhiemVuKhacHoanThanh ?? 0),
      nhiemVuKhacDangThucHien: toNum(x?.nhiemVuKhacDangThucHien ?? x?.NhiemVuKhacDangThucHien ?? 0),
    }));
  }, [tongHopNhanVien]);

  const teamOptions = useMemo(() => {
    const seen = new Map();

    rows.forEach((r) => {
      if (r.teamId == null || r.teamId === '') {
        return;
      }

      const value = String(r.teamId);
      const label = r.tenTeam && String(r.tenTeam).trim();

      if (!label) return;

      // Loại bỏ hẳn lựa chọn "Chưa gán nhóm"
      if (value === '0') return;
      if (label.toLowerCase() === 'chưa gán nhóm') return;

      if (!seen.has(value)) {
        seen.set(value, label);
      }
    });

    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), 'vi'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!teamFilter) return rows;

    // if (teamFilter === TEAM_FILTER_NO_TEAM) {
    //   return rows.filter((r) => r.teamId == null || r.teamId === '');
    // }

    return rows.filter((r) => String(r.teamId) === String(teamFilter));
  }, [rows, teamFilter]);

  const selectedTeamLabel = useMemo(
    () => teamOptions.find((x) => x.value === teamFilter)?.label || '',
    [teamOptions, teamFilter]
  );

  const detailPath =
    URI_INTERNAL.BAOCAO_CHI_TIET_NHAN_VIEN_PATH ||
    `${URI_INTERNAL.BAOCAO_THONGKE_PATH}/chi-tiet-nhan-vien`;

  const moduleCellTdStyle = {
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: '6px',
    paddingRight: '6px',
    verticalAlign: 'middle',
  };

  const summaryCellTdStyle = {
    verticalAlign: 'middle',
  };

  const renderModuleStatusCell = (done, doing) => (
    <div className="d-flex justify-content-center align-items-center">
      <div
        title={`Hoàn thành: ${done} | Đang thực hiện: ${doing}`}
        style={{
          position: 'relative',
          width: '88px',
          height: '40px',
          minWidth: '88px',
          border: '1px solid #bfc5cc',
          borderRadius: '3px',
          overflow: 'hidden',
          boxSizing: 'border-box',
          backgroundColor: 'transparent',
        }}
      >
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        >
          <line
            x1="0"
            y1="100"
            x2="100"
            y2="0"
            stroke="#495057"
            strokeWidth="2"
          />
        </svg>

        <span
          className="text-success font-weight-bold"
          style={{
            position: 'absolute',
            top: '4px',
            left: '10px',
            fontSize: '16px',
            lineHeight: 1,
          }}
        >
          {done}
        </span>

        <span
          className="text-danger font-weight-bold"
          style={{
            position: 'absolute',
            right: '10px',
            bottom: '4px',
            fontSize: '16px',
            lineHeight: 1,
          }}
        >
          {doing}
        </span>
      </div>
    </div>
  );

  return (
    <Card id="bao-cao-tong-hop-nhan-vien">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="w-100">
          <h3 className="card-title mb-0 float-none">Báo cáo chi tiết tất cả nhân viên</h3>
          {/* <div className="text-muted d-block" style={{ marginTop: 4 }}>
            Tổng hợp toàn bộ công việc của tất cả thành viên
          </div> */}
        </div>
      </Card.Header>

      <Card.Body>
        <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
          <div style={{ minWidth: 220 }}>
            <label className="form-label mb-1">Khoảng thời gian</label>
            <select
              className="form-select form-select-sm"
              value={rangeLabel}
              onChange={handleChangeRange}
              disabled={loadingTongHopNhanVien}
            >
              <option value="">-- Chọn --</option>
              {RANGE_OPTIONS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: 220 }}>
            <label className="form-label mb-1">Nhóm</label>
            <select
              className="form-select form-select-sm"
              value={teamFilter}
              onChange={handleChangeTeamFilter}
              disabled={loadingTongHopNhanVien}
            >
              <option value="">-- Tất cả nhóm --</option>
              {teamOptions.map((x) => (
                <option key={x.value} value={x.value}>
                  {x.label}
                </option>
              ))}
            </select>
          </div>

          {rangeLabel === RANGE_CUSTOM && (
            <>
              <div>
                <label className="form-label mb-1">Từ ngày</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={loadingTongHopNhanVien}
                />
              </div>

              <div>
                <label className="form-label mb-1">Đến ngày</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={loadingTongHopNhanVien}
                />
              </div>

              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleApplyCustomRange}
                disabled={loadingTongHopNhanVien}
              >
                Lọc
              </button>
            </>
          )}

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleClearFilter}
            disabled={loadingTongHopNhanVien}
          >
            Xóa lọc
          </button>

          {/* {selectedTeamLabel && (
            <div className="filter-hint" style={{ width: '100%' }}>
              Nhóm đang lọc: {selectedTeamLabel}
            </div>
          )} */}

          {(appliedFrom || appliedTo) && (
            <div className="filter-hint" style={{ width: '100%' }}>
              Đang lọc: {fmtDDMMYYYY(appliedFrom) || '...'} → {fmtDDMMYYYY(appliedTo) || '...'}
            </div>
          )}
        </div>

        {loadingTongHopNhanVien ? (
          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            <Spinner animation="border" size="sm" />
            <span>Đang tải dữ liệu...</span>
          </div>
        ) : (
          <Table
            striped
            bordered
            hover
            className="text-center align-middle w-100"
            style={{ tableLayout: 'fixed' }}
          >
            <colgroup>
              <col style={{ width: '60px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '100px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '100px' }} />
            </colgroup>

            <thead>
              <tr>
                <th className="sorting">STT</th>
                <th className="sorting">Thành viên</th>
                <th className="sorting">Tổng công việc</th>
                <th className="sorting">Hoàn thành</th>
                <th className="sorting">Đang thực hiện</th>
                <th className="sorting">Phần mềm</th>
                <th className="sorting">Xử lý yêu cầu</th>
                <th className="sorting">Hỗ trợ</th>
                <th className="sorting">Nhiệm vụ khác</th>
                <th className="sorting">Chức năng</th>
              </tr>
            </thead>

            {filteredRows.length > 0 ? (
              <tbody>
                {filteredRows.map((r, idx) => (
                  <tr key={`${r.nhanVienId}-${idx}`}>
                    <td style={summaryCellTdStyle}>{idx + 1}</td>

                    <td className="text-center" style={summaryCellTdStyle}>
                      <span className="d-block w-100 text-truncate" title={r.tenThanhVien}>
                        {r.tenThanhVien}
                      </span>
                    </td>

                    <td className="text-primary font-weight-bold" style={summaryCellTdStyle}>
                      {r.tongCongViec}
                    </td>
                    <td className="text-success font-weight-bold" style={summaryCellTdStyle}>
                      {r.hoanThanh}
                    </td>
                    <td className="text-danger font-weight-bold" style={summaryCellTdStyle}>
                      {r.dangThucHien}
                    </td>

                    <td style={moduleCellTdStyle}>
                      {renderModuleStatusCell(r.phanMemHoanThanh, r.phanMemDangThucHien)}
                    </td>
                    <td style={moduleCellTdStyle}>
                      {renderModuleStatusCell(r.xuLyYeuCauHoanThanh, r.xuLyYeuCauDangThucHien)}
                    </td>
                    <td style={moduleCellTdStyle}>
                      {renderModuleStatusCell(r.hoTroHoanThanh, r.hoTroDangThucHien)}
                    </td>
                    <td style={moduleCellTdStyle}>
                      {renderModuleStatusCell(r.nhiemVuKhacHoanThanh, r.nhiemVuKhacDangThucHien)}
                    </td>

                    <td>
                      <ul className="nav justify-content-center">
                        <li className="nav-item">
                          <NavLink
                            to={`${detailPath}?nhanVienId=${r.nhanVienId}${
                              r.teamId != null && r.teamId !== '' ? `&teamId=${r.teamId}` : ''
                            }${appliedFrom ? `&From=${encodeURIComponent(appliedFrom)}` : ''}${
                              appliedTo ? `&To=${encodeURIComponent(appliedTo)}` : ''
                            }${appliedRangeLabel ? `&RangeLabel=${encodeURIComponent(appliedRangeLabel)}` : ''}&source=tong-hop-tat-ca&sourceTab=bao-cao-nhan-vien&returnUrl=${returnUrl}`}
                            exact
                            className="nav-link"
                            title="Xem chi tiết"
                          >
                            <i className="nav-icon font-icon fas fa-eye" />
                          </NavLink>
                        </li>
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody>
                <tr className="txt-center">
                  <td colSpan="10">Không có dữ liệu</td>
                </tr>
              </tbody>
            )}

            <tfoot>
              <tr>
                <th className="sorting">STT</th>
                <th className="sorting">Thành viên</th>
                <th className="sorting">Tổng công việc</th>
                <th className="sorting">Hoàn thành</th>
                <th className="sorting">Đang thực hiện</th>
                <th className="sorting">Phần mềm</th>
                <th className="sorting">Xử lý yêu cầu</th>
                <th className="sorting">Hỗ trợ</th>
                <th className="sorting">Nhiệm vụ khác</th>
                <th className="sorting">Chức năng</th>
              </tr>
            </tfoot>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

export default BaoCaoTongHopNhanVien;